// Specs of the archetype-3 (threshold action list) documents: no KPI strip
// (a stat in the header instead), the list across pages. Pure (see
// ./shared.ts).
import { translate } from "shared/languages";
import { formatDateOrDash } from "shared/reports/columnCatalogue";
import {
  ClientInactiveAnalytics,
  PackageAnalyticsExpired,
  PackageExpiryRow,
  PackageLowCreditsAnalytics,
} from "shared/types";
import { SHARED_LABEL } from "shared/utils";
import { DocSpec } from "../../pagePlan";
import { truncationNote } from "../../reportText";
import { SortMeta } from "../../sort";
import { columns, productNoun } from "./shared";

// "Tidak Terbatas" for a membership's credits, as the screen prints.
const creditsOrUnlimited = (v: number | null | undefined) =>
  v === null || v === undefined ? translate("analyticsPage.package.unlimited") : String(v);

// These lists print every loaded row by default; the note only appears when
// the admin's row choice cut the list short, so loaded = total (no
// sorted-from-loaded guard, as before).
const listNote = (shown: number, total: number, noun: string) =>
  truncationNote({ shown, total, noun, loaded: total, sortedOffDefault: false });

// ── Klien Tidak Aktif ───────────────────────────────────────────────────────

type InactiveRow = ClientInactiveAnalytics["table"][number];

const INACTIVE_META = (): SortMeta => ({
  days_inactive: { label: translate("analyticsPage.columns.days_inactive"), type: "number" },
});

export const inactiveSpec: DocSpec<ClientInactiveAnalytics, InactiveRow> = {
  layout: "detail",
  limited: true,
  meta: INACTIVE_META,
  table: (data) => data.table,
  columns: () =>
    columns<InactiveRow>([
      { metric: "client", render: (r) => r.customer_name },
      { metric: "days_inactive", render: (r) => String(r.days_inactive) },
      { metric: "last_product", render: (r) => r.last_package_name },
      { metric: "expiry", render: (r) => formatDateOrDash(r.expired_at) },
    ]),
  kpis: () => [],
  defaultSort: { key: "days_inactive", order: "descend" },
  note: (data, shown) => listNote(shown, data.table.length, translate("pdf.noun.klien")),
};

// ── Kredit Akan Habis (paket only) ──────────────────────────────────────────

// A package more than one person loses when it expires is marked on screen
// with a "Shared" chip beside the buyer's name; the PDF prints the same fixed
// English word as the name cell's sub line. The buyer stays the named client:
// they paid, so they are who the studio calls about a renewal.
const sharedSub = (r: PackageExpiryRow): string | null =>
  (r.shared_with_count ?? 1) > 1 ? SHARED_LABEL : null;

type LowCreditsRow = PackageLowCreditsAnalytics["table"][number];

const LOW_CREDITS_META = (): SortMeta => ({
  remaining_credits: { label: translate("analyticsPage.columns.remaining_credits"), type: "number" },
  expiry_date: { label: translate("analyticsPage.columns.expiry"), type: "date-future" },
});

export const lowCreditsSpec: DocSpec<PackageLowCreditsAnalytics, LowCreditsRow> = {
  layout: "detail",
  limited: true,
  meta: LOW_CREDITS_META,
  table: (data) => data.table,
  // The screen's order: the client, the package, then the metric.
  columns: () =>
    columns<LowCreditsRow>([
      { metric: "client", render: (r) => r.customer_name, sub: sharedSub },
      { metric: "product", header: productNoun("paket"), render: (r) => r.package_name },
      { metric: "remaining_credits", render: (r) => creditsOrUnlimited(r.remaining_credits) },
      { metric: "expiry", render: (r) => formatDateOrDash(r.expiry_date) },
      { metric: "last_used", render: (r) => formatDateOrDash(r.last_used) },
    ]),
  kpis: () => [],
  defaultSort: { key: "remaining_credits", order: "descend" },
  note: (data, shown) => listNote(shown, data.table.length, translate("pdf.noun.paket")),
};

// ── Kedaluwarsa / Akan Kedaluwarsa / Belum Aktif ────────────────────────────

export type ExpiryWhich = "expired" | "expiring_soon" | "unactivated";
type ExpiryRow = PackageAnalyticsExpired["expired"][number];

// Expired = past dates (recency wording); expiring soon = future dates
// (proximity wording).
const expiryMeta = (which: ExpiryWhich): SortMeta =>
  which === "unactivated"
    ? {
        auto_start_date: { label: translate("analyticsPage.columns.auto_start"), type: "date-future" },
      }
    : {
        expiry_date: {
          label: translate("analyticsPage.columns.expiry"),
          type: which === "expired" ? "date" : "date-future",
        },
      };

export const expirySpec = (
  type: "paket" | "membership",
  which: ExpiryWhich,
): DocSpec<PackageAnalyticsExpired, ExpiryRow> => {
  const isMembership = type === "membership";
  const creditsCol = isMembership
    ? []
    : [
        {
          metric: "remaining_credits" as const,
          render: (r: ExpiryRow) => creditsOrUnlimited(r.remaining_credits),
        },
      ];
  return {
    layout: "detail",
    limited: true,
    meta: () => expiryMeta(which),
    table: (data) => data[which],
    columns: () =>
      which === "unactivated"
        ? columns<ExpiryRow>([
            // No running clock, so no expiry column: purchase date and the day
            // the clock starts on its own instead (matches the screen).
            { metric: "client", render: (r) => r.customer_name, sub: sharedSub },
            { metric: "product", header: productNoun(type), render: (r) => r.package_name },
            { metric: "purchase_date", render: (r) => formatDateOrDash(r.purchase_date ?? null) },
            { metric: "auto_start", render: (r) => formatDateOrDash(r.auto_start_date ?? null) },
            ...creditsCol,
          ])
        : columns<ExpiryRow>([
            { metric: "client", render: (r) => r.customer_name, sub: sharedSub },
            { metric: "expiry", render: (r) => formatDateOrDash(r.expiry_date) },
            { metric: "product", header: productNoun(type), render: (r) => r.package_name },
            ...creditsCol,
            { metric: "last_used", render: (r) => formatDateOrDash(r.last_used) },
          ]),
    kpis: () => [],
    defaultSort: {
      key: which === "unactivated" ? "auto_start_date" : "expiry_date",
      order: which === "expired" ? "descend" : "ascend",
    },
    note: (data, shown) => listNote(shown, data[which].length, translate(`pdf.noun.${type}`)),
  };
};
